class MarmetoOrderListing extends HTMLElement {
  constructor() {
    super();

    this.pageSize = 5;
    this.BASE_URL = "https://tesseract-v2.curefit.co";

    this.itemsHolder = this.querySelector("items-holder");
    this.paginationHolder = this.querySelector("pagination-holder");
    const userDataScript = this.querySelector('script[type="application/json"]');
    if (!this.itemsHolder || !this.paginationHolder || !userDataScript) return;

    this.previousButton = this.paginationHolder.querySelector(
      ".js-button-previous",
    );
    this.nextButton = this.paginationHolder.querySelector(".js-button-next");
    if (!this.previousButton || !this.nextButton) return;
    this.userDetails = JSON.parse(userDataScript.textContent);

    this.currentPage = this.getCurrentPageFromQuery() || 0;
    this._requestSeq = 0;
    this._feedbackAutoOpenTimer = null;
    this._feedbackAutoOpenOrderId = null;
    this._feedbackAutoOpenCompletedOrderId = null;
    this._feedbackAutoOpenAttempts = 0;

    this.feedbackUrlMap = JSON.parse(document.getElementById('order-feedback-url-map')?.textContent || '{}');

    this.initPagination();
    this.getOrders(this.currentPage, this.pageSize);

    // Handle browser back/forward on paginated orders URLs.
    // bottom-nav.js handles the button/message path; this covers native browser gestures.
    window.addEventListener("popstate", () => {
      if (window.location.pathname === "/pages/orders") {
        const page = this.getCurrentPageFromQuery() || 0;
        this.currentPage = page;
        this.getOrders(page, this.pageSize, { scrollToTop: true });
      }
    });
  }

  disconnectedCallback() {
    if (this._feedbackAutoOpenTimer) {
      window.clearTimeout(this._feedbackAutoOpenTimer);
      this._feedbackAutoOpenTimer = null;
    }
  }

  /**
   * Gets the current page number from the query parameters.
   * @returns {number|null} - The page number if present in the query string, or null if not found.
   */
  getCurrentPageFromQuery() {
    const params = new URLSearchParams(window.location.search);
    const page = params.get("page");
    return page ? parseInt(page, 10) : null;
  }

  /**
   * Updates the URL to reflect the current page number.
   * Uses replaceState when the URL already shows this page (e.g. triggered from
   * a popstate handler) to avoid adding a duplicate history entry.
   * @param {number} pageNumber - The current page number.
   */
  updateUrlWithPageNumber(pageNumber) {
    const url = new URL(window.location);
    const existingPage = parseInt(url.searchParams.get("page") || "0", 10);
    url.searchParams.set("page", pageNumber);
    if (existingPage === pageNumber) {
      window.history.replaceState({}, "", url);
    } else {
      window.history.pushState({}, "", url);
    }
  }

  /**
   * Initializes pagination buttons
   */
  initPagination() {
    this.previousButton.addEventListener(
      "click",
      this.debounce(() => this.handlePrevious(), 300),
    );
    this.nextButton.addEventListener(
      "click",
      this.debounce(() => this.handleNext(), 300),
    );
  }

  /**
   * Fetches orders from the order ledger and updates the UI.
   * @param {number} pageNumber - The page number to fetch.
   * @param {number} limit - The number of orders to fetch per page.
   */
  async getOrders(pageNumber = 0, limit = 5, { scrollToTop = false } = {}) {
    const seq = ++this._requestSeq;
    const orderListUrl = `${this.BASE_URL}/orders/external/list?cfUserId=${this.userDetails.userId}&pageNumber=${pageNumber}&limit=${limit}`;

    const shopifyYCookie = getCookie("_shopify_y");
    const requestHeaders = new Headers({
      timestamp: window.marmetoSecrets.timestamp,
      hash: window.marmetoSecrets.hashKey,
      usertokenhashkey: window.marmetoSecrets.userTokenHashKey,
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Device-ID": shopifyYCookie ?? "",
    });

    const requestOptions = {
      method: "GET",
      headers: requestHeaders,
      redirect: "follow",
    };

    try {
      const response = await fetch(orderListUrl, requestOptions);
      const responseJson = await response.json();

      if (responseJson.statusCode === 498) {
        console.log("Status code: " + responseJson.statusCode);
      }

      const data = responseJson.data?.orders;
      console.log("Fetched Orders Data:", data);

      // Discard stale responses from earlier rapid taps / concurrent calls.
      if (seq !== this._requestSeq) return;

      this.updateStateFromResponse(data);
      this.renderOrders(data.items);

      // Scroll after the DOM has been painted so the new content is in place.
      if (scrollToTop) {
        requestAnimationFrame(() => {
          if (window.location.pathname === '/pages/orders') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }

      // Purchase analytics are fired from the app bridge via BREEZE_PURCHASE_COMPLETE.
      // On the orders redirect we only clear the temporary purchase state.
      if (localStorage.getItem("app-purchase") === "true") {
        localStorage.removeItem("app-purchase");
        localStorage.removeItem("gaPxCart");
        localStorage.removeItem("gaPxCartItems");
      }
      
      this.updatePagination(data);
    } catch (error) {
      this.handleFetchError(error);
    }
  }

  /**
   * Updates pagination state and size from the API response.
   * @param {Object} data - The response data.
   */
  updateStateFromResponse(data) {
    this.currentPage = data.pageNumber;
    this.updateUrlWithPageNumber(this.currentPage);
  }

  /**
   * Renders the fetched orders inside the items-holder.
   * @param {Array} orderItems - List of order items.
   */
  renderOrders(orderItems) {
    if (!orderItems.length) {
      this.itemsHolder.innerHTML = `
        <div class="order-item">
          <div class="align-center order-item__empty">
            <h5 class="">You haven't placed any orders yet</h5>
            <div class="rte">
              <p>Check out your recently viewed items or explore our categories to find something you'll love!</p>
            </div>
            <div class="button-row">
              <a data-event-type="link" data-event-name="Back To Homepage" class="btn btn--primary button-row__btn" href="/">Back To Homepage</a>
            </div>
          </div>
        </div>
      `;
      this.hidePagination();
      return;
    }

    const renderProductInfo = (item) => {
      return `
      ${item.productBrand ? `<div class="order-item__brand">${item.productBrand}</div>` : ""}
      ${item.productName ? `<div class="order-item__title">${item.productName}</div>` : ""}
      ${item.productSize ? `<div class="order-item__size">Size: ${item.productSize}</div>` : ""}
      <div class="order-item__details">
        ${
          item.channel === "SHOPIFY"
            ? `<a
          data-event-type="link"
          data-event-name="Clickpost Product"
          href="${item.channelDetailsPageUrl}"
          class="view-details-button"
          data-order-id="${item.orderId}"
        >
          View Details
        </a>`
            : `<button
          type="button"
          class="view-details-button view-details-button--disabled"
          data-order-id="${item.orderId}"
          aria-disabled="true"
          disabled
        >
          View Details
        </button>`
        }${
        Object.prototype.hasOwnProperty.call(this.feedbackUrlMap, item.orderId)
          ? `<div class="feed-back-button-wrapper"><button
        type="button"
        class="rate-button js-feedback-button"
        data-feedback-url="${this.feedbackUrlMap[item.orderId] || ''}"
        data-order-number="${this.getOrderNumber(item)}"
          >Rate Order</button></div>`
          : ''
        }
      </div>
      `;
    };

    const ordersHTML = orderItems
      .map((item) => {
        const statusClass = this.getItemStatus(item.itemStatusInfo.state);
        const statusIcon = this.getStatusIcon(statusClass);

        return `
        <div class="order-item" data-order-id="${item.orderName ? item.orderName : item.orderId}" data-order-channel="${item.channel}" data-order-state="${item.itemStatusInfo.state}">
          <div class="order-item__id">Order ID ${item.orderName ? item.orderName : item.orderId}</div>
          <div class="order-item__header ${statusClass} flex items-center">
            <div class="order-item__icon flex items-center justify-center">
              ${statusIcon}
            </div>
            <div class="order-item__status">
              <div class="order-item__status-heading">${item.itemStatusInfo.edd_info}</div>
              <div class="order-item__status-subheading">${item.itemStatusInfo.title}</div>
            </div>
          </div>
          <div class="order-item__content flex items-center mt-6">
            <a data-event-type="link" data-event-name="Clickpost Product" href="/products/${item.productSlug}" class="order-item__image block">
              <div class="order-item__image block">
                <img src="${item.productImageLink}" alt="${item.productBrand}" height="125" width="100">
              </div>
             </a>
            <div class="order-item__info block">
              ${renderProductInfo(item)}
            </div>
          </div>
        </div>
      `;
      })
      .join("");

    this.itemsHolder.innerHTML = ordersHTML;

    // Add event listeners for feedback buttons
    this.itemsHolder.querySelectorAll('.js-feedback-button').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const feedbackUrl = btn.getAttribute('data-feedback-url');
        const orderNumber = btn.getAttribute('data-order-number');
        this.openFeedbackModal(feedbackUrl, orderNumber);
      });
    });

    // WebViews can paint the page and modal on different ticks.
    requestAnimationFrame(() => {
      this.scheduleFeedbackAutoOpen();
    });
  }

  /**
   * Returns a CSS class based on the order status.
   * @param {string} state - The order status.
   * @returns {string} CSS class.
   */
  getItemStatus(state) {
    const statusClassMapping = {
      error: new Set([
        "failed_delivery",
        "failed_pickup",
        "pickup_canceled",
        "act_as_delivered",
        "qc_rejected",
        "canceled",
        "rto_initiated",
        "payment_pending",
        "payment_under_review",
        "payment_failed",
      ]),
      success: new Set([
        "backordered",
        "onhand",
        "on_hand",
        "packed",
        "shipped",
        "out_for_delivery",
        "out_for_pickup",
        "delivered",
        "ready_to_ship",
      ]),
      neutral: new Set([
        "return_initiated",
        "exchange_initiated",
        "qc_approved",
        "received",
        "picked_up",
        "exchange_picked_up",
        "return_approved",
      ]),
    };

    if (statusClassMapping.error.has(state)) return "status--error";
    if (statusClassMapping.success.has(state)) return "status--success";
    if (statusClassMapping.neutral.has(state)) return "status--neutral";
    return "";
  }

  /**
   * Returns an SVG string representing a status icon.
   * @param {string} statusClass - A class name for the status of an order item.
   * @returns {string} An SVG string representing a status icon.
   */
  getStatusIcon(statusClass) {
    return statusClass === "status--error"
      ? `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`
      : `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>`;
  }

  /**
   * Updates the pagination buttons based on the current page and response data.
   * @param {Object} data - The pagination data returned from the API.
   */
  updatePagination(data) {
    this.toggleButtonState(this.previousButton, data.pageNumber === 0);
    this.toggleButtonState(this.nextButton, data.lastPage);

    if (data.items.length > 0) {
      this.showPagination();
    } else {
      this.hidePagination();
    }
  }

  /**
   * Toggles the "disabled" attribute on the button based on the condition.
   * @param {HTMLElement} button - The button to toggle.
   * @param {boolean} condition - If true, the button will be disabled; otherwise, it will be enabled.
   */
  toggleButtonState(button, condition) {
    button.disabled = condition;
  }

  /**
   * Handles "previous" button click event.
   */
  handlePrevious() {
    if (!this.previousButton.disabled) {
      this.currentPage = Math.max(this.currentPage - 1, 0);
      this.getOrders(this.currentPage, this.pageSize, { scrollToTop: true });
    }
  }

  /**
   * Handles "next" button click event.
   */
  handleNext() {
    if (!this.nextButton.disabled) {
      this.currentPage += 1;
      this.getOrders(this.currentPage, this.pageSize, { scrollToTop: true });
    }
  }

  /**
   * Shows the pagination controls after data is loaded.
   */
  showPagination() {
    this.paginationHolder.classList.remove("hidden");
  }

  /**
   * Hides the pagination controls.
   */
  hidePagination() {
    this.paginationHolder.classList.add("hidden");
  }

  /**
   * Debounces a function to avoid multiple rapid executions.
   * @param {Function} func - The function to debounce.
   * @param {number} delay - The debounce delay in milliseconds.
   * @returns {Function} - The debounced function.
   */
  debounce(func, delay) {
    let debounceTimer;
    return function (...args) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(this, args), delay);
    };
  }

  /**
   * Handles fetch errors.
   * @param {Error} error - The error object.
   */
  handleFetchError(error) {
    console.error("Error fetching orders:", error);
  }

  /**
   * Returns the user details object, which contains the user ID.
   * @returns {Object} - The user details object.
   */
  getUserDetails() {
    return this.userDetails;
  }

  getOrderNumber(item) {
    return String(item?.orderName ?? item?.orderId ?? '').replace(/^#/, '');
  }

  showFeedbackSubmittedToast() {
    window.csToast?.showToast?.('warning', 'Your feedback has already been submitted. Thank you!');
  }

  buildFeedbackUrl(feedbackUrl, orderNumber) {
    if (!feedbackUrl) return '';

    try {
      const url = new URL(feedbackUrl);
      if (orderNumber) {
        url.searchParams.set('order_number', String(orderNumber).replace(/^#/, ''));
      }
      return url.toString();
    } catch (error) {
      console.error('Invalid feedback URL:', feedbackUrl, error);
      return feedbackUrl;
    }
  }

  openFeedbackModal(feedbackUrl, orderNumber) {
    const modal = document.getElementById('ModalFeedbackForm');
    const iframe = document.getElementById('ModalFeedbackFormIframe');
    const resolvedFeedbackUrl = this.buildFeedbackUrl(feedbackUrl, orderNumber);

    if (!resolvedFeedbackUrl) {
      this.showFeedbackSubmittedToast();
      return true;
    }

    if (!modal || !iframe) {
      return;
    }

    iframe.src = resolvedFeedbackUrl;
    modal.scrollY = window.scrollY;
    modal.style.display = 'flex';
    modal.setAttribute('open', true);
    document.body.classList.add('fixed');
    document.body.style.top = `-${modal.scrollY}px`;
    document.body.style.overflow = 'hidden';

    return true;
  }

  normalizeOrderId(orderId) {
    return String(orderId ?? '').trim().replace(/^#/, '');
  }

  findMatchingOrderItemByOrderId(orderId) {
    const normalizedOrderId = this.normalizeOrderId(orderId);

    if (!normalizedOrderId || !this.itemsHolder) {
      return null;
    }

    const orderItems = this.itemsHolder.querySelectorAll('.order-item[data-order-id]');
    return Array.from(orderItems).find((orderItem) => {
      const itemOrderId = this.normalizeOrderId(
        orderItem.getAttribute('data-order-id') || orderItem.dataset?.orderId,
      );

      return itemOrderId === normalizedOrderId;
    }) || null;
  }

  checkUrlForOrderIdAndTriggerFeedback() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlOrderId = urlParams.get('order_id');

      if (!urlOrderId) {
        console.debug('No order_id found in URL query parameters.');
        return;
      }

      const normalizedOrderId = this.normalizeOrderId(urlOrderId);
      if (!normalizedOrderId) {
        console.info('Skipping feedback modal auto-open because normalized order_id is empty.');
        return;
      }

      if (this._feedbackAutoOpenCompletedOrderId === normalizedOrderId) {
        return true;
      }

      if (!this.itemsHolder) {
        console.error('itemsHolder element is not defined or not found.');
        return false;
      }

      const matchingOrderItem = this.findMatchingOrderItemByOrderId(normalizedOrderId);

      if (!matchingOrderItem) {
        return false;
      }

      const feedbackButton = matchingOrderItem.querySelector('.feed-back-button-wrapper button, .feed-back-button-wrapper a');

      if (!feedbackButton) {
        return false;
      }

      // Open the modal directly so this does not depend on WebView synthetic-click handling.
      const didOpenFeedbackModal = this.openFeedbackModal(
        feedbackButton.getAttribute('data-feedback-url'),
        feedbackButton.getAttribute('data-order-number'),
      );

      if (!didOpenFeedbackModal) {
        return false;
      }

      // The redirect query is the one-shot signal. Consume it after a successful open.
      this._feedbackAutoOpenCompletedOrderId = normalizedOrderId;
      this.removeOrderIdFromUrl(normalizedOrderId);
      return true;
    } catch (error) {
      console.error('Error in checkUrlForOrderIdAndTriggerFeedback:', error);
      return false;
    }
  }

  removeOrderIdFromUrl(orderId) {
    try {
      const url = new URL(window.location.href);
      if (this.normalizeOrderId(url.searchParams.get('order_id')) !== this.normalizeOrderId(orderId)) {
        return;
      }

      url.searchParams.delete('order_id');
      window.history.replaceState(window.history.state, document.title, url.href);
    } catch (error) {
      // Keep the in-memory completion guard active if history is unavailable.
      console.warn('Unable to consume feedback order_id from URL:', error);
    }
  }

  scheduleFeedbackAutoOpen() {
    const urlOrderId = new URLSearchParams(window.location.search).get('order_id');
    const normalizedOrderId = this.normalizeOrderId(urlOrderId);

    if (!normalizedOrderId) {
      return;
    }

    if (this._feedbackAutoOpenOrderId === normalizedOrderId && this._feedbackAutoOpenTimer) {
      return;
    }

    if (this._feedbackAutoOpenTimer) {
      window.clearTimeout(this._feedbackAutoOpenTimer);
      this._feedbackAutoOpenTimer = null;
    }

    this._feedbackAutoOpenOrderId = normalizedOrderId;
    this._feedbackAutoOpenAttempts = 0;

    const attempt = () => {
      if (this.checkUrlForOrderIdAndTriggerFeedback()) {
        this._feedbackAutoOpenTimer = null;
        return;
      }

      this._feedbackAutoOpenAttempts += 1;
      if (this._feedbackAutoOpenAttempts >= 20) {
        this._feedbackAutoOpenTimer = null;
        console.warn(`Unable to auto-open feedback modal for order_id: ${urlOrderId}`);

        const userAgent = navigator.userAgent || '';
        const platform = /android/i.test(userAgent)
          ? 'android'
          : /iphone|ipad|ipod/i.test(userAgent)
            ? 'ios'
            : 'web';

        window.CultStoreSentry?.captureSilentFailure?.(
          'Feedback modal did not auto-open after checkout completion',
          {
            type: 'checkout_feedback_modal_not_opened',
            component: 'order_listing_feedback_auto_open',
            modalId: 'ModalFeedbackForm',
            iframeId: 'ModalFeedbackFormIframe',
            orderId: normalizedOrderId,
            attempts: this._feedbackAutoOpenAttempts,
            pageUrl: window.location.href,
            platform,
            runtime: window.ReactNativeWebView ? 'app' : 'web',
          },
        );

        return;
      }

      this._feedbackAutoOpenTimer = window.setTimeout(() => {
        window.requestAnimationFrame(attempt);
      }, 250);
    };

    attempt();
  }
}

customElements.define("marmeto-order-listing", MarmetoOrderListing);

class MarmetoOrderItem extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", () => this.initiateRedirection());
  }

  extractOrderToken(url) {
    if (!url) return null;

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
      return pathParts[pathParts.length - 1] || null;
    } catch (error) {
      console.error("Failed to extract order token:", error);
      return null;
    }
  }

  async initiateRedirection() {
    const url = this.dataset.url;
    const userDetails = document
      .querySelector("marmeto-order-listing")
      .getUserDetails();
    const orderToken = this.extractOrderToken(url);

    const shopifyYCookie = getCookie("_shopify_y");
    if (url && orderToken && userDetails?.userId) {
      fetch("https://tesseract-v2.curefit.co/orders/external/details/multipass", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          timestamp: window.marmetoSecrets.timestamp,
          hash: window.marmetoSecrets.hashKey,
          usertokenhashkey: window.marmetoSecrets.userTokenHashKey,
          "X-Device-ID": shopifyYCookie ?? "",
        },
        body: JSON.stringify({
          orderToken,
          cfUserId: userDetails.userId,
        }),
      })
        .then((response) => response.json())
        .then((responseJson) => {
          const redirectUrl = responseJson?.data?.multipassUrl || url;
          window.open(redirectUrl, "_blank");
        })
        .catch((error) => console.error("Error:", error));
    }
  }
}

customElements.define("marmeto-order-item", MarmetoOrderItem);
